import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Key, Shield, Clock, Users, CheckCircle, ChevronRight,
  AlertTriangle, Lock, Unlock, RefreshCw, UserCheck, UserX, Eye,
  FileText, Settings, Zap, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const lifecycleStages = [
  {
    icon: FileText,
    title: 'Request',
    description: 'User submits access request via any intake channel. System captures justification and requested scope.',
    details: ['Self-service portal', 'Manager delegation', 'Bulk requests', 'Role catalog browsing']
  },
  {
    icon: Users,
    title: 'Guidance',
    description: 'System analyzes request against role catalog. Suggests appropriate entitlements based on peer analysis.',
    details: ['Role recommendations', 'Peer comparison', 'Scope refinement', 'Least privilege suggestions']
  },
  {
    icon: CheckCircle,
    title: 'Approval',
    description: 'Request routes to appropriate approvers based on risk level and organizational rules.',
    details: ['Multi-level approval', 'Delegation chains', 'SLA enforcement', 'Escalation rules']
  },
  {
    icon: Zap,
    title: 'Provisioning',
    description: 'Approved access is provisioned via IdP groups, SCIM, direct API, or manual steps.',
    details: ['IdP group assignment', 'SCIM provisioning', 'Direct API calls', 'Manual task tracking']
  },
  {
    icon: Eye,
    title: 'Audit',
    description: 'All access changes logged with full evidence. Compliance reports generated automatically.',
    details: ['Change logging', 'Evidence capture', 'Compliance reports', 'Anomaly detection']
  },
  {
    icon: RefreshCw,
    title: 'Review',
    description: 'Periodic access reviews ensure continued appropriateness. Managers certify or revoke access.',
    details: ['Scheduled campaigns', 'Manager certification', 'Auto-revoke on non-response', 'Review history']
  },
  {
    icon: UserX,
    title: 'Revocation',
    description: 'Access removed on termination, role change, or manual request. Clean-up verified.',
    details: ['Automatic triggers', 'Grace periods', 'Clean-up verification', 'Orphan detection']
  }
];

const guardrails = [
  {
    icon: Shield,
    title: 'Least Privilege',
    description: 'System recommends minimum required access. Warns when requests exceed peer baseline.',
    enforcement: 'Auto-suggest reduced scope'
  },
  {
    icon: Users,
    title: 'Separation of Duties',
    description: 'Detects toxic combinations of entitlements. Blocks or escalates conflicting access.',
    enforcement: 'Block with exception workflow'
  },
  {
    icon: Lock,
    title: 'MFA Requirements',
    description: 'Validates MFA enrollment before granting sensitive access. Enforces step-up authentication.',
    enforcement: 'Gate provisioning on MFA'
  },
  {
    icon: Clock,
    title: 'Time-Bound Access',
    description: 'Elevated access granted with automatic expiration. Renewal requires re-justification.',
    enforcement: 'Auto-revoke on expiry'
  },
  {
    icon: AlertTriangle,
    title: 'Break-Glass',
    description: 'Emergency access bypasses normal approval but triggers enhanced monitoring and review.',
    enforcement: 'Post-hoc review required'
  }
];

const provisioningPaths = [
  {
    method: 'IdP Groups',
    description: 'Add user to IdP group that grants application access. Fastest path for SSO-enabled apps.',
    apps: 'Okta, Entra ID, Google Workspace'
  },
  {
    method: 'SCIM',
    description: 'Provision user directly in application via SCIM protocol. Supports role and profile sync.',
    apps: 'Salesforce, Slack, Zoom, Dropbox'
  },
  {
    method: 'Direct API',
    description: 'Application-specific API calls for fine-grained provisioning. Custom connectors available.',
    apps: 'AWS IAM, GitHub, Jira, custom apps'
  },
  {
    method: 'Manual Steps',
    description: 'When automation isn\'t available, system tracks manual tasks with SLA and verification.',
    apps: 'Legacy systems, physical access, vendor portals'
  }
];

const accessTypes = [
  { type: 'Standard Access', approval: 'Manager', timeline: 'Immediate', example: 'Basic SaaS tools' },
  { type: 'Elevated Access', approval: 'Manager + Security', timeline: 'Same day', example: 'Admin consoles' },
  { type: 'Privileged Access', approval: 'Manager + Security + Exec', timeline: '24-48 hours', example: 'Production systems' },
  { type: 'Break-Glass', approval: 'Self-approved, post-review', timeline: 'Immediate', example: 'Emergency response' },
  { type: 'Time-Bound', approval: 'Manager', timeline: 'Per request', example: 'Project access' },
  { type: 'Contractor', approval: 'Sponsor + Compliance', timeline: '24 hours', example: 'External access' }
];

const faqs = [
  {
    question: 'What happens when an access request is denied?',
    answer: 'The requester receives notification with the denial reason. They can modify the request and resubmit, escalate to a different approver, or appeal through a defined exception workflow.'
  },
  {
    question: 'How do you handle access for contractors and external users?',
    answer: 'External users require a sponsor (internal employee) who remains accountable for the access. Additional approval gates, shorter access durations, and enhanced monitoring apply by default.'
  },
  {
    question: 'Can access be granted before the user\'s start date?',
    answer: 'Yes. Joiner workflows can be triggered by HRIS events before the start date. Access is provisioned but remains inactive until the start date, when it\'s automatically enabled.'
  },
  {
    question: 'How are access reviews different from certification campaigns?',
    answer: 'Access reviews are continuous, triggered by events like role changes. Certification campaigns are periodic (quarterly/annual) where managers review all access for their reports in batch.'
  },
  {
    question: 'What\'s the blast radius for a privileged access grant?',
    answer: 'The system calculates blast radius based on the systems affected, data sensitivity, and number of users impacted. High blast radius requires additional approvals and generates alerts to security.'
  }
];

export const AccessManagement = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/product" className="hover:text-foreground">Product</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Access Management</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Full Identity Lifecycle
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              From request to revocation. Guide users to appropriate access, enforce approval policies,
              provision via multiple paths, and maintain continuous compliance.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/identity">View Identity Dashboard</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Lifecycle */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Access Lifecycle Stages</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {lifecycleStages.map((stage, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5 relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {idx + 1}
                    </div>
                    <stage.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{stage.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{stage.description}</p>
                  <ul className="space-y-1">
                    {stage.details.map((detail, didx) => (
                      <li key={didx} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle className="w-3 h-3 text-primary" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guardrails */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Governance Guardrails</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guardrails.map((guardrail, idx) => (
                <div key={idx} className="border border-border rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <guardrail.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{guardrail.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{guardrail.description}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-foreground">Enforcement:</span>
                    <span className="text-muted-foreground">{guardrail.enforcement}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Provisioning Paths */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Provisioning Paths</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {provisioningPaths.map((path, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{path.method}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{path.description}</p>
                  <div className="text-xs">
                    <span className="font-medium text-foreground">Examples: </span>
                    <span className="text-muted-foreground">{path.apps}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Access Types Table */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Access Type Matrix</h2>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-12 bg-secondary px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border">
                <div className="col-span-3">Access Type</div>
                <div className="col-span-3">Approval Chain</div>
                <div className="col-span-3">Timeline</div>
                <div className="col-span-3">Example</div>
              </div>
              {accessTypes.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-0 text-sm hover:bg-secondary/30 transition-colors">
                  <div className="col-span-3 font-medium text-foreground">{item.type}</div>
                  <div className="col-span-3 text-muted-foreground">{item.approval}</div>
                  <div className="col-span-3 text-muted-foreground">{item.timeline}</div>
                  <div className="col-span-3 text-muted-foreground">{item.example}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blocked Actions */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Blocked Actions Show Required Role</h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  When a user attempts an action they're not authorized for, the system shows exactly what
                  role or approval is required. Users can request the necessary access directly from the block message.
                </p>
                <ul className="space-y-3">
                  {[
                    'Clear explanation of why action is blocked',
                    'Required role or permission displayed',
                    'One-click access request from block message',
                    'Link to request status after submission'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-destructive" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">Action Blocked</div>
                    <div className="text-xs text-muted-foreground">Insufficient privileges</div>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-foreground">Attempted action: </span>
                    <span className="text-muted-foreground">Modify production IAM policy</span>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Required role: </span>
                    <span className="text-muted-foreground">Cloud Admin or Security Engineer</span>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Approval required: </span>
                    <span className="text-muted-foreground">Manager + Security Team</span>
                  </div>
                </div>
                <Button className="w-full mt-4" size="sm">
                  Request Access
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Automate Your Access Lifecycle</h2>
            <p className="text-muted-foreground mb-8">
              See how organizations reduce access request time from days to minutes while maintaining compliance.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/product/automation-builder">Automation Builder</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default AccessManagement;
